import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  Typography,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  AccountBalance as AccountBalanceIcon,
  Store as StoreIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  SwapHoriz as SwapHorizIcon,
  Rule as RuleIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Save as SaveIcon,
  TaskAlt as TaskAltIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { QA_STATUS_LABELS as STATUS_LABELS } from '../../utils/documentQaStatus';

// Criterios reales del formato FR-O-07 "Revisión de PIPC" (Rev.08) del cliente -- mismo catálogo
// que el backend (com.conem.enums.QaCriteria), solo se usa aquí para el texto/orden/agrupación por
// sección; el estado real (hallazgo por criterio) vive en document_qa_findings y se carga vía
// GET /document-qa/{id}/checklist. Se conservan los códigos y los saltos de numeración tal como
// están en el documento original (ej. E salta de E02 a E04, H salta a J02). Ronda 1 = Fase I
// (secciones A-E), Ronda 2 = Fase II (secciones F-H) -- FASE 0 (revisión por IA) queda fuera del
// sistema.
const QA_CHECKLIST_SECTIONS = [
  {
    round: 1, code: 'A', label: 'Presentación y Control Documental',
    items: [
      { code: 'A01', text: 'Existe congruencia entre el índice, la numeración y el orden real de las páginas.' },
      { code: 'A02', text: 'No se observan faltas de ortografía, errores de captura o textos incompletos relevantes.' },
      { code: 'A03', text: 'Pie de página, logotipo, folio, razón social y datos visibles corresponden al expediente revisado.' },
      { code: 'A04', text: 'No existen páginas en blanco injustificadas, tablas cortadas, textos recortados o imágenes sobrepuestas.' },
      { code: 'A05', text: 'Márgenes, alineación, encabezados, pies de página y formato general son consistentes.' },
      { code: 'A06', text: 'La revisión, fecha y paginación son trazables en todas las páginas.' },
    ]
  },
  {
    round: 1, code: 'B', label: 'Datos Generales',
    items: [
      { code: 'B01', text: 'El nombre del representante legal, responsables y encargado del PIPC es consistente en todo el programa.' },
      { code: 'B02', text: 'Razón social y RFC coinciden entre datos generales, tablas y anexos visibles.' },
      { code: 'B03', text: 'Domicilio completo y municipio coinciden en texto, tablas, directorios, croquis y anexos visibles.' },
      { code: 'B04', text: 'Georreferencia y coordenadas en decimales son consistentes en todas sus apariciones.' },
      { code: 'B05', text: 'Superficies y aforos coinciden entre apartados y tablas.' },
      { code: 'B06', text: 'Horarios y población fija o flotante mantienen cifras congruentes.' },
      { code: 'B07', text: 'Teléfonos y correos de responsables están completos y son consistentes.' },
      { code: 'B08', text: 'Giro y actividad descrita corresponden al mismo inmueble en todo el documento.' },
    ]
  },
  {
    round: 1, code: 'C', label: 'Análisis de Riesgo',
    items: [
      { code: 'C01', text: 'Los fenómenos perturbadores analizados guardan relación con la zona geográfica localizada.' },
      { code: 'C02', text: 'Las sustancias, equipos o procesos mencionados corresponden a los riesgos analizados.' },
      { code: 'C03', text: 'Frecuencia y consecuencia mantienen una relación interna comprensible y no contradictoria.' },
      { code: 'C04', text: 'Las medidas de mitigación responden a los escenarios identificados y no son únicamente genéricas.' },
      { code: 'C05', text: 'El análisis de riesgo de incendio citado está desarrollado y el cálculo es correcto.' },
      { code: 'C06', text: 'Texto, tablas, mapas y representaciones gráficas de los fenómenos perturbadores son congruentes.' },
      { code: 'C07', text: 'No se detectan referencias a otros giros, municipios, inmuebles o equipos incompatibles.' },
    ]
  },
  {
    round: 1, code: 'D', label: 'Brigadas y Recursos',
    items: [
      { code: 'D01', text: 'Las brigadas mencionadas se encuentran en el listado de recursos humanos.' },
      { code: 'D02', text: 'Los recursos mencionados en los planes aparecen en los inventarios visibles.' },
      { code: 'D03', text: 'Inventarios, croquis y mapas ubican los mismos equipos y señalización.' },
      { code: 'D04', text: 'Las capacitaciones y capacidades mencionadas cuentan con soporte documental visible.' },
      { code: 'D05', text: 'La posible insuficiencia de recursos se registra como hallazgo o RVH. No se presume suficiencia operativa.' },
    ]
  },
  {
    round: 1, code: 'E', label: 'Planes de Emergencia',
    items: [
      { code: 'E01', text: 'Los procedimientos responden a los riesgos identificados en el análisis.' },
      { code: 'E02', text: 'Las secuencias de actuación mantienen un orden operativo comprensible y no contradictorio.' },
      { code: 'E04', text: 'Los códigos de alertamiento y denominaciones son consistentes entre todos los planes.' },
      { code: 'E05', text: 'El croquis de evacuación y repliegue corresponde al inmueble, rutas, salidas y puntos descritos.' },
      { code: 'E06', text: 'Los teléfonos de emergencia corresponden al municipio indicado en el expediente.' },
      { code: 'E07', text: 'Los planes y recomendaciones están dirigidos al personal que realmente interviene.' },
      { code: 'E08', text: 'No existen procedimientos aparentemente genéricos, reciclados o incompatibles con el inmueble.' },
    ]
  },
  {
    round: 2, code: 'F', label: 'Impresión y Armado',
    items: [
      { code: 'F01', text: 'La impresión está completa, legible y sin páginas, tablas o imágenes defectuosas.' },
      { code: 'F02', text: 'El orden físico coincide con el índice y los separadores o anexos están identificados.' },
      { code: 'F03', text: 'Cliente, folio, revisión y número de página permiten identificar cada hoja del expediente.' },
    ]
  },
  {
    round: 2, code: 'G', label: 'Firmas y Sellos',
    items: [
      { code: 'G01', text: 'Carta de corresponsabilidad: firmas autógrafas originales y sello troquelado, cuando así lo indique el expediente o listado aplicable.' },
      { code: 'G02', text: 'Constancias de capacitación: firmas originales y sello troquelado, cuando así lo indique el expediente o listado aplicable.' },
      { code: 'G03', text: 'Acta UIPC: firmas autógrafas de los integrantes indicados y congruencia con el directorio.' },
      { code: 'G04', text: 'Nombres, cargos y fechas de las firmas son congruentes con el resto del PIPC.' },
    ]
  },
  {
    round: 2, code: 'H', label: 'Integración de Anexos Técnicos',
    items: [
      { code: 'H01', text: 'Registro del tercero acreditado: copia vigente anexada, con marca de agua.' },
      { code: 'J02', text: 'Dictamen estructural vigente.' },
      { code: 'J03', text: 'Dictamen eléctrico vigente.' },
      { code: 'J04', text: 'Dictamen de gas vigente cuando aplique.' },
      { code: 'J05', text: 'Póliza de seguro: copia legible y vigente.' },
      { code: 'J06', text: 'Evidencia de extintores: carta responsiva y/o factura de recarga vigente.' },
      { code: 'J07', text: 'Los anexos corresponden a la razón social, domicilio e inmueble revisados.' },
      { code: 'J08', text: 'El listado FR-O-16 utilizado está anexado, identificado y con número de revisión.' },
    ]
  },
];

// Sin selección = Cumple (no existe fila en document_qa_findings para ese criterio).
// CORRECCION_REQUERIDA y RVH bloquean aprobar la ronda mientras no se marquen resueltos
// (ver DocumentQaFinding.Status.blocksApproval en el backend); NICE_TO_HAVE y NA nunca bloquean.
const QA_FINDING_STATUS_OPTIONS = [
  { value: 'CORRECCION_REQUERIDA', label: 'Corrección', color: '#c62828', bg: '#ffebee', title: 'Corrección requerida (bloquea aprobar)' },
  { value: 'NICE_TO_HAVE', label: 'Nice to have', color: '#1565c0', bg: '#e3f2fd', title: 'Mejora sugerida (no bloquea)' },
  { value: 'NA', label: 'NA', color: '#757575', bg: '#f5f5f5', title: 'No aplica (requiere justificación)' },
  { value: 'RVH', label: 'RVH', color: '#e65100', bg: '#fff3e0', title: 'Requiere validación humana (bloquea aprobar)' },
];
const BLOCKING_STATUSES = new Set(['CORRECCION_REQUERIDA', 'RVH']);

const DOC_LABELS = {
  PIPC: 'PIPC',
  REPORTE_INSPECCION: 'RIC',
  ACTA_CONSTITUTIVA_UIPC: 'Acta Constitutiva',
  CARTA_CORRESPONSABILIDAD: 'Carta de Corresponsabilidad'
};
const docLabel = (key) => DOC_LABELS[key] || (key?.startsWith('CONSTANCIAS_CURSO_') ? 'Constancias' : key);

// Mismo patrón de "saltar directo al campo" que usa "Validar Campos" en Órdenes de Trabajo
// (Programas.jsx) -- reutilizado aquí para que Validación de Campos en Revisión de Calidad no sea
// solo texto plano, sino que lleve al revisor directo a corregir. Copiado (no importado) porque
// Programas.jsx lo mantiene como closures internas atadas a su propio estado; ver comentario
// homólogo ahí si algún día se extrae a un módulo compartido.
const getReadinessIcon = (percentage) => {
  if (percentage >= 100) return <CheckCircleIcon sx={{ color: '#4caf50' }} fontSize="small" />;
  if (percentage >= 60) return <WarningIcon sx={{ color: '#ff9800' }} fontSize="small" />;
  return <ErrorIcon sx={{ color: '#f44336' }} fontSize="small" />;
};

const getReadinessColor = (percentage) => {
  if (percentage >= 100) return '#4caf50';
  if (percentage >= 60) return '#ff9800';
  return '#f44336';
};

const stripBraces = (key) => key.replace(/^\{|\}$/g, '');

const COMPANY_FIELD_OVERRIDES = {
  '{LATITUD}': 'GEOREFERENCIA',
  '{LONGITUD}': 'GEOREFERENCIA',
  '{empresa_imagen}': 'IMAGEN_INMUEBLE',
  '{HORARIO_TRABAJO}': 'HORARIO_OPERATIVO',
  '{RESPONSABLE_SUCURSAL}': 'CONTACTO_CORPORATIVO',
  '{TELEFONO_SUCURSAL}': 'TELEFONO_CORPORATIVO',
  '{EMAIL_SUCURSAL}': 'EMAIL_CORPORATIVO',
};
const COMPANY_FIELD_KEYS = new Set([
  '{NOMBRE_EMPRESA}', '{RAZON_SOCIAL}', '{RFC}', '{GIRO_EMPRESA}', '{CONTACTO_CORPORATIVO}',
  '{EMAIL_CORPORATIVO}', '{TELEFONO_CORPORATIVO}', '{LOGO_EMPRESA}', '{COMPANIA_SEGUROS}',
  '{NUMERO_POLIZA}', '{VIGENCIA_POLIZA}', '{MUNICIPIO}', '{GEOREFERENCIA}', '{LATITUD}', '{LONGITUD}',
  '{AREA_CONSTRUCCION}', '{AREA_TERRENO}', '{NIVELES_CONSTRUCCION}', '{empresa_imagen}',
  '{REGIMEN_PROPIEDAD}', '{NUMERO_EMPLEADOS}', '{POBLACION_FLOTANTE}', '{HORARIO_TRABAJO}',
  '{HORARIO_ADMINISTRATIVO}', '{CAPACIDAD_MAXIMA}', '{ACTIVIDADES_PRINCIPALES}',
  '{RESPONSABLE_SUCURSAL}', '{TELEFONO_SUCURSAL}', '{EMAIL_SUCURSAL}',
]);
const getCompanyDomFieldKey = (fieldKey) => COMPANY_FIELD_OVERRIDES[fieldKey] || stripBraces(fieldKey);

const BRANCH_FIELD_OVERRIDES = {
  '{LATITUD}': 'GEOREFERENCIA',
  '{LONGITUD}': 'GEOREFERENCIA',
  '{DIRECCION_INMUEBLE}': 'DIRECCION_FISICA_SUCURSAL',
  '{empresa_imagen}': 'IMAGEN_INMUEBLE',
};
const BRANCH_FIELD_KEYS = new Set([
  '{GIRO_EMPRESA}', '{MUNICIPIO}', '{GEOREFERENCIA}', '{LATITUD}', '{LONGITUD}',
  '{DIRECCION_FISICA_SUCURSAL}', '{DIRECCION_INMUEBLE}', '{RESPONSABLE_SUCURSAL}',
  '{TELEFONO_SUCURSAL}', '{EMAIL_SUCURSAL}', '{COMPANIA_SEGUROS}', '{NUMERO_POLIZA}',
  '{VIGENCIA_POLIZA}', '{AREA_CONSTRUCCION}', '{AREA_TERRENO}', '{NIVELES_CONSTRUCCION}',
  '{REGIMEN_PROPIEDAD}', '{NUMERO_EMPLEADOS}', '{CAPACIDAD_MAXIMA}', '{POBLACION_FLOTANTE}',
  '{HORARIO_TRABAJO}', '{HORARIO_ADMINISTRATIVO}', '{ACTIVIDADES_PRINCIPALES}', '{empresa_imagen}',
]);
const getBranchDomFieldKey = (fieldKey) => BRANCH_FIELD_OVERRIDES[fieldKey] || stripBraces(fieldKey);

const EVALUATION_FIELD_KEYS = new Set([
  '{SUSTANCIAS_PELIGROSAS}', '{CAPACIDAD_GAS}', '{vb}', '{TRABAJADORES_DISCAPACIDAD}',
]);
const AGENDA_FIELD_KEYS = new Set(['{NIVEL_RIESGO}']);

// ctx: { workOrderId, quotationId, companyId, companyName }
const getFieldJumpTarget = (fieldKey, entityType, entityId, ctx) => {
  if (AGENDA_FIELD_KEYS.has(fieldKey)) {
    return {
      path: '/servicios/agenda',
      label: 'Agenda de Servicios',
      state: {
        highlightWorkOrderId: ctx.workOrderId,
        highlightEntityType: entityType,
        highlightBranchId: entityType === 'SUCURSAL' ? entityId : null,
        highlightFieldKey: stripBraces(fieldKey),
      },
    };
  }
  if (EVALUATION_FIELD_KEYS.has(fieldKey)
      || /^\{(ESTRUCTURAL|EQUIPO_ESPECIFICO|INSTALACION_ELECTRICA|INSTALACION_GAS|RECIPIENTES_PRESION)_/.test(fieldKey)) {
    return {
      path: '/evaluaciones/inspeccion',
      label: 'Evaluación de Inspección',
      state: {
        quotationId: ctx.quotationId,
        companyId: ctx.companyId,
        companyName: ctx.companyName,
        branchId: entityType === 'SUCURSAL' ? entityId : null,
        entityType,
        highlightFieldKey: stripBraces(fieldKey),
      },
    };
  }
  if (entityType === 'SUCURSAL' && BRANCH_FIELD_KEYS.has(fieldKey)) {
    return {
      path: '/clientes/sucursales',
      label: 'Gestión de Sucursales',
      state: { highlightEntityId: entityId, entityType, highlightFieldKey: getBranchDomFieldKey(fieldKey) },
    };
  }
  if (COMPANY_FIELD_KEYS.has(fieldKey)) {
    return {
      path: '/clientes/empresas',
      label: 'Catálogo de Empresas',
      state: {
        highlightEntityId: entityType === 'SUCURSAL' ? ctx.companyId : entityId,
        entityType: 'MATRIZ',
        highlightFieldKey: getCompanyDomFieldKey(fieldKey),
      },
    };
  }
  return null;
};

const getSectionRoute = (sectionName, entityType) => {
  const sectionMap = {
    'Información de la Empresa': { path: '/clientes/empresas', label: 'Catálogo de Empresas' },
    'Información del Inmueble': entityType === 'SUCURSAL'
      ? { path: '/clientes/sucursales', label: 'Gestión de Sucursales' }
      : { path: '/clientes/empresas', label: 'Catálogo de Empresas' },
    'Información Operativa': entityType === 'SUCURSAL'
      ? { path: '/clientes/sucursales', label: 'Gestión de Sucursales' }
      : { path: '/clientes/empresas', label: 'Catálogo de Empresas' },
    'Información del Documento': { path: '/estadisticas', label: 'Gestión de Cotizaciones' },
    'Información del PIPC': { path: null, label: 'Editar PIPC en esta pantalla' },
    'Tablas Dinámicas': { path: null, label: 'Editar en esta pantalla' },
  };
  return sectionMap[sectionName] || null;
};

const groupMissingFieldsBySection = (fields) => {
  const bySection = {};
  (fields || []).forEach((f) => {
    if (!bySection[f.section]) bySection[f.section] = [];
    bySection[f.section].push(f);
  });
  return bySection;
};

const TABS = [
  { key: 'documento', label: 'Documento', icon: <DescriptionIcon fontSize="small" /> },
  { key: 'validacion', label: 'Validación de Campos', icon: <AssignmentTurnedInIcon fontSize="small" /> },
  { key: 'checklist', label: 'Formato de Revisión', icon: <RuleIcon fontSize="small" /> },
  { key: 'versiones', label: 'Versiones', icon: <HistoryIcon fontSize="small" /> },
  { key: 'notas', label: 'Notas', icon: <CommentIcon fontSize="small" /> },
];

// Progreso simulado para la subida a Dropbox: no hay forma de medir el avance real del tramo
// backend->Dropbox (onUploadProgress de axios solo cubre navegador->backend, que en local es casi
// instantáneo). Avanza por pasos aleatorios que se hacen más chicos según se acerca al tope de
// 95% -- da sensación de avance real sin prometer una cifra exacta. Solo el 100% real llega al
// terminar la petición (ver handleReplaceFile).
const startFakeProgress = (setProgress) => {
  setProgress(0);
  return setInterval(() => {
    setProgress(prev => {
      if (prev >= 95) return prev;
      const remaining = 95 - prev;
      const step = Math.max(1, Math.round(Math.random() * remaining * 0.15));
      return Math.min(95, prev + step);
    });
  }, 450);
};

const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Diálogo de revisión de calidad, con el mismo contenido en pestañas que la pantalla completa
// (Documento / Validación de Campos / Formato de Revisión / Notas) -- Omar prefirió el formato
// modal sobre pantalla completa: se abre encima de la lista en vez de navegar a otra pantalla.
const RevisionCalidadDialog = ({ open, quotationDocumentId, onClose, onUpdated }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState('documento');

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Validación de campos (RIC/PIPC) del ente del documento en revisión -- para que el revisor
  // confirme contra la realidad en vez de solo aprobar/rechazar a ciegas.
  const [fieldValidation, setFieldValidation] = useState(null);
  const [fieldValidationLoading, setFieldValidationLoading] = useState(false);
  // Archivo revisado vs. el mismo documento generado hoy (se pide al abrir la pestaña; tarda unos segundos)
  const [fileComparison, setFileComparison] = useState({ forId: null, loading: false, data: null, job: null });
  // Comparación en curso: al cambiar de documento o cerrar, se deja de consultar su avance
  const comparisonRunRef = useRef(0);

  // Reemplazo manual: descargar, editar fuera del sistema, y volver a subir con el mismo nombre.
  const [replacing, setReplacing] = useState(false);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const replaceInputRef = useRef(null);

  // Checklist de "Formato de Revisión": hallazgos reales por criterio (document_qa_findings).
  // findings: { [codigo]: DocumentQaFindingDTO } -- ausente = Cumple. drafts: { [codigo]: { status, note } }
  // guarda la edición en curso de un renglón antes de guardarla (para no golpear el backend en
  // cada tecla).
  const [findings, setFindings] = useState({});
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [findingBusy, setFindingBusy] = useState({});
  const [expandedSection, setExpandedSection] = useState(null);

  const getDraft = (code) => drafts[code] ?? (findings[code]
    ? { status: findings[code].status, note: findings[code].note }
    : { status: null, note: '' });

  const isDraftDirty = (code) => {
    const draft = getDraft(code);
    const finding = findings[code];
    return draft.status !== (finding?.status || null) || draft.note !== (finding?.note || '');
  };

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const docId = selected?.quotationDocumentId;
    if (tab === 'validacion' && docId && fileComparison.forId !== docId) {
      fetchFileComparison(docId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selected?.quotationDocumentId]);

  const renderFileComparison = () => {
    const { loading, data, job } = fileComparison;
    const box = { mb: 2.5, p: 1.5, borderRadius: 2, border: '1px solid' };
    if (loading || !data) {
      const steps = job?.steps || [];
      const currentIdx = steps.findIndex(st => st.key === job?.currentStep);
      return (
        <Box sx={{ ...box, borderColor: '#90caf9', bgcolor: '#f5f9ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Revisando el documento… {job ? `(${job.elapsedSeconds} s)` : ''}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Este proceso puede tardar <strong>varios minutos</strong>. Se revisan <strong>todos los campos</strong> del documento y,
            además, el archivo que estás revisando se compara contra el <strong>machote llenado con los datos actuales</strong>,
            renglón por renglón, para detectar lo que cambió después de generarlo. Puedes seguir en las otras pestañas mientras termina.
          </Typography>
          {steps.map((st, i) => {
            const done = currentIdx > i;
            const current = currentIdx === i;
            return (
              <Box key={st.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                <Box sx={{ width: 18, display: 'flex', justifyContent: 'center' }}>
                  {done ? <span>✅</span> : current ? <CircularProgress size={13} /> : <span style={{ color: '#bdbdbd' }}>○</span>}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: current ? 700 : 400, color: done || current ? 'text.primary' : 'text.disabled' }}>
                  {i + 1}. {st.label}{current && job?.detail ? ` — ${job.detail}` : ''}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    const retry = (
      <Button size="small" onClick={() => fetchFileComparison(selected.quotationDocumentId, { force: true })} sx={{ textTransform: 'none' }}>Volver a comparar</Button>
    );
    if (data.status === 'UNAVAILABLE') {
      return (
        <Alert severity="info" sx={{ mb: 2.5 }} action={retry}>
          No se comparó el archivo con los datos: {data.message}
        </Alert>
      );
    }
    if (data.status === 'SAME') {
      return (
        <Alert severity="success" sx={{ mb: 2.5 }} action={retry}>
          <strong>El archivo está al día:</strong> es igual a lo que se generaría hoy con los datos del sistema
          {data.linesCompared ? ` (${data.linesCompared.toLocaleString('es-MX')} renglones revisados)` : ''}.
          {' '}Por eso el resultado de campos de abajo aplica tal cual a este archivo.
          {data.checkedAt && ` (Comprobado ${new Date(data.checkedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}.)`}
        </Alert>
      );
    }
    return (
      <Box sx={{ ...box, borderColor: '#ffb74d', bgcolor: '#fff8e1' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100' }}>
            ⚠️ El archivo difiere en {data.totalDifferences} punto{data.totalDifferences === 1 ? '' : 's'} de lo que el sistema generaría hoy.
            Puede ser porque los datos cambiaron después de generarlo{data.fileDateKnown ? ` (el ${data.fileDate})` : ''} o porque
            el archivo se editó a mano. Revisa cada punto; si no se justifica, rechaza para que se regenere.
            {data.checkedAt && ` (Comprobado ${new Date(data.checkedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}.)`}
          </Typography>
          {retry}
        </Box>
        <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>En el archivo</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>Con los datos de hoy</Typography>
          {(data.differences || []).map((d, i) => (
            <React.Fragment key={i}>
              <Typography variant="body2" sx={{ p: 0.75, bgcolor: '#ffebee', borderRadius: 1, whiteSpace: 'pre-line', color: d.file ? 'text.primary' : 'text.secondary', fontStyle: d.file ? 'normal' : 'italic' }}>
                {d.file || '(no aparece)'}
              </Typography>
              <Typography variant="body2" sx={{ p: 0.75, bgcolor: '#e8f5e9', borderRadius: 1, whiteSpace: 'pre-line', color: d.current ? 'text.primary' : 'text.secondary', fontStyle: d.current ? 'normal' : 'italic' }}>
                {d.current || '(ya no aparece)'}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
        {data.totalDifferences > (data.differences || []).length && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Se muestran las primeras {(data.differences || []).length} de {data.totalDifferences} diferencias.
          </Typography>
        )}
      </Box>
    );
  };

  const fetchFieldValidation = useCallback(async (item) => {
    if (!item.workOrderId) return;
    setFieldValidationLoading(true);
    try {
      const res = await api.get(`/files/work-order/${item.workOrderId}/validate`);
      const entity = item.branchId
        ? (res.data.branches || []).find(b => String(b.entityId) === String(item.branchId))
        : res.data.matriz;
      // Validación del documento que se revisa (antes Carta y Acta caían en la del RIC)
      const VALIDATION_BY_DOCUMENT = {
        REPORTE_INSPECCION: 'ric', PIPC: 'pipc', CARTA_CORRESPONSABILIDAD: 'carta', ACTA_CONSTITUTIVA_UIPC: 'acta'
      };
      const validationKey = VALIDATION_BY_DOCUMENT[item.documentKey];
      const docValidation = validationKey ? entity?.[validationKey] : null;
      setFieldValidation(docValidation || null);
    } catch (e) {
      setFieldValidation(null);
    } finally {
      setFieldValidationLoading(false);
    }
  }, []);

  // Corre en el servidor en segundo plano (puede tardar minutos) y aquí se consulta su avance
  const fetchFileComparison = useCallback(async (id, { force = false } = {}) => {
    const run = ++comparisonRunRef.current;
    setFileComparison({ forId: id, loading: true, data: null, job: null });
    // Resultado precalculado en segundo plano (al generar/reemplazar): si es reciente se usa tal cual
    if (!force) {
      try {
        const latest = await api.get(`/document-qa/${id}/file-comparison/latest`);
        const checkedAt = latest.status === 200 && latest.data?.checkedAt ? new Date(latest.data.checkedAt) : null;
        if (checkedAt && Date.now() - checkedAt.getTime() < 30 * 60 * 1000) {
          if (run === comparisonRunRef.current) setFileComparison({ forId: id, loading: false, data: latest.data, job: null });
          return;
        }
      } catch {
        // sin resultado guardado: se compara ahora
      }
      if (run !== comparisonRunRef.current) return;
    }
    const fail = (e) => setFileComparison({ forId: id, loading: false, job: null,
      data: { status: 'UNAVAILABLE', message: e?.response?.data?.error || 'No se pudo comparar el archivo con los datos actuales.' } });
    try {
      let job = (await api.post(`/document-qa/${id}/file-comparison/start`)).data;
      while (run === comparisonRunRef.current) {
        if (job.status !== 'RUNNING') {
          setFileComparison({ forId: id, loading: false, data: job.result, job });
          return;
        }
        setFileComparison({ forId: id, loading: true, data: null, job });
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (run !== comparisonRunRef.current) return;
        job = (await api.get(`/document-qa/file-comparison/jobs/${job.jobId}`)).data;
      }
    } catch (e) {
      if (run === comparisonRunRef.current) fail(e);
    }
  }, []);

  useEffect(() => () => { comparisonRunRef.current += 1; }, []);

  const fetchNotes = useCallback(async (id) => {
    setNotesLoading(true);
    try {
      const res = await api.get(`/document-qa/${id}/notes`);
      setNotes(res.data || []);
    } catch (e) {
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const fetchVersions = useCallback(async (id) => {
    setVersionsLoading(true);
    try {
      const res = await api.get(`/document-qa/${id}/versions`);
      setVersions(res.data || []);
    } catch (e) {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  const fetchChecklist = useCallback(async (id) => {
    setFindingsLoading(true);
    try {
      const res = await api.get(`/document-qa/${id}/checklist`);
      const map = {};
      (res.data || []).forEach(item => { if (item.finding) map[item.code] = item.finding; });
      setFindings(map);
    } catch (e) {
      setFindings({});
    } finally {
      setFindingsLoading(false);
    }
  }, []);

  const setBusy = (code, value) => setFindingBusy(prev => ({ ...prev, [code]: value }));

  const handleDraftStatus = (code, status) => {
    // ToggleButtonGroup exclusive: volver a tocar el botón ya seleccionado manda null -> el
    // reviewer quiere borrar el hallazgo (volver a "Cumple").
    if (status === null) {
      handleClearFinding(code);
      return;
    }
    setDrafts(prev => ({ ...prev, [code]: { ...getDraft(code), status } }));
  };

  const handleDraftNote = (code, note) => {
    setDrafts(prev => ({ ...prev, [code]: { ...getDraft(code), note } }));
  };

  const handleSaveFinding = async (code) => {
    const draft = getDraft(code);
    if (!draft.status || !draft.note.trim()) return;
    setBusy(code, true);
    try {
      const res = await api.post(`/document-qa/${selected.quotationDocumentId}/findings/${code}`, {
        status: draft.status, note: draft.note.trim()
      });
      setFindings(prev => ({ ...prev, [code]: res.data }));
      setDrafts(prev => { const next = { ...prev }; delete next[code]; return next; });
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Error al guardar el hallazgo', severity: 'error' });
    } finally {
      setBusy(code, false);
    }
  };

  const handleClearFinding = async (code) => {
    if (!findings[code]) {
      // No había nada guardado todavía, solo se estaba editando -- no hay que llamar al backend.
      setDrafts(prev => { const next = { ...prev }; delete next[code]; return next; });
      return;
    }
    setBusy(code, true);
    try {
      await api.delete(`/document-qa/${selected.quotationDocumentId}/findings/${code}`);
      setFindings(prev => { const next = { ...prev }; delete next[code]; return next; });
      setDrafts(prev => { const next = { ...prev }; delete next[code]; return next; });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al quitar el hallazgo', severity: 'error' });
    } finally {
      setBusy(code, false);
    }
  };

  const handleResolveFinding = async (code) => {
    setBusy(code, true);
    try {
      const res = await api.post(`/document-qa/${selected.quotationDocumentId}/findings/${code}/resolve`);
      setFindings(prev => ({ ...prev, [code]: res.data }));
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al marcar el hallazgo como resuelto', severity: 'error' });
    } finally {
      setBusy(code, false);
    }
  };

  // Se reinicia todo cada vez que se abre el diálogo o cambia el documento (a diferencia de la
  // versión de pantalla completa, este componente sigue montado entre aperturas/cierres).
  useEffect(() => {
    if (!open || !quotationDocumentId) return;
    setLoading(true);
    setLoadError(null);
    setTab('documento');
    setFieldValidation(null);
    setFindings({});
    setDrafts({});
    setNewNote('');
    (async () => {
      try {
        const res = await api.get(`/document-qa/${quotationDocumentId}`);
        setSelected(res.data);
        fetchFieldValidation(res.data);
        fetchNotes(quotationDocumentId);
        fetchVersions(quotationDocumentId);
        fetchChecklist(quotationDocumentId);
      } catch (e) {
        setLoadError(e.response?.data?.error || 'No se pudo cargar el documento');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quotationDocumentId]);

  const currentRound = (item) => {
    if (!item) return null;
    if (item.qaStatus === 'PENDIENTE_REVISION' || item.qaStatus === 'REVISION_1_RECHAZADA') return 1;
    if (item.qaStatus === 'REVISION_1_APROBADA' || item.qaStatus === 'REVISION_2_RECHAZADA') return 2;
    return null; // REVISION_2_APROBADA: ya no hay ronda pendiente
  };

  const handleReview = async (approved) => {
    const round = currentRound(selected);
    if (!round) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/document-qa/${selected.quotationDocumentId}/review`, {
        round, approved, note: newNote.trim() || null
      });
      setSnackbar({ open: true, message: `✅ Ronda ${round} ${approved ? 'aprobada' : 'rechazada'}`, severity: 'success' });
      setSelected(res.data);
      setNewNote('');
      fetchNotes(selected.quotationDocumentId);
      onUpdated?.(res.data);
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al registrar la revisión';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !selected) return;
    setReplacing(true);
    const progressTimer = startFakeProgress(setReplaceProgress);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/document-qa/${selected.quotationDocumentId}/replace`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Los PIPC/RIC pueden pesar decenas/hasta ~150MB (límite configurado) -- 30-60s no
        // alcanza para subir eso en una conexión lenta. Con progreso real, mejor dar minutos que
        // cortar la conexión mientras el backend sigue trabajando (ver feedback de Omar: el
        // reemplazo terminaba bien en el backend, pero el frontend lo daba por caído).
        timeout: 300000,
      });
      clearInterval(progressTimer);
      setReplaceProgress(100);
      setSelected(res.data);
      setSnackbar(res.data.replaced === false
        ? { open: true, message: 'No se reemplazó: el archivo subido es idéntico al vigente.', severity: 'info' }
        : {
            open: true,
            message: res.data.contentChanged === true
              ? '✅ Documento reemplazado — se detectó un cambio de contenido respecto a la versión anterior'
              : '✅ Documento reemplazado correctamente',
            severity: 'success'
          });
      fetchNotes(res.data.quotationDocumentId);
      fetchVersions(res.data.quotationDocumentId);
      onUpdated?.(res.data);
    } catch (err) {
      clearInterval(progressTimer);
      const msg = err.response?.data?.error || 'Error al reemplazar el documento';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setReplacing(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selected) return;
    setActionLoading(true);
    try {
      await api.post(`/document-qa/${selected.quotationDocumentId}/notes`, { note: newNote.trim() });
      setNewNote('');
      fetchNotes(selected.quotationDocumentId);
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar la nota', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Fuerza descarga directa en vez de la vista previa de Dropbox (dl=0 -> dl=1)
  const getDownloadLink = (dropboxLink) => {
    if (!dropboxLink) return null;
    if (/[?&]dl=1/.test(dropboxLink)) return dropboxLink;
    if (/[?&]dl=0/.test(dropboxLink)) return dropboxLink.replace(/dl=0/, 'dl=1');
    return dropboxLink + (dropboxLink.includes('?') ? '&dl=1' : '?dl=1');
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Encabezado de sección de Validación de Campos, con salto directo a la pantalla donde se
  // corrige (mismo patrón que "Validar Campos" en Órdenes de Trabajo) -- cierra el diálogo antes
  // de navegar para no dejar un modal huérfano detrás.
  const renderSectionHeader = (sectionName, entityType, entityId, color, fields, ctx) => {
    const firstFieldJump = fields && fields.length > 0 ? getFieldJumpTarget(fields[0].key, entityType, entityId, ctx) : null;
    const route = getSectionRoute(sectionName, entityType);
    const target = firstFieldJump
      ? { path: firstFieldJump.path, state: firstFieldJump.state, label: firstFieldJump.label || sectionName }
      : (route && route.path ? { path: route.path, state: { highlightEntityId: entityId, entityType }, label: route.label } : null);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">{sectionName}:</Typography>
        {target && (
          <Tooltip title={`Ir a ${target.label}`}>
            <IconButton
              size="small"
              onClick={() => { onClose(); navigate(target.path, { state: target.state }); }}
              sx={{ p: 0.2, color: color || '#1976d2' }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderMissingFieldsList = (fields, entityType, entityId, color, ctx) => (
    fields.map((f, i) => {
      const jumpTarget = getFieldJumpTarget(f.key, entityType, entityId, ctx);
      if (jumpTarget) {
        return (
          <Typography
            key={i}
            variant="caption"
            display="block"
            onClick={() => { onClose(); navigate(jumpTarget.path, { state: jumpTarget.state }); }}
            sx={{ ml: 1, color, cursor: 'pointer', textDecoration: 'underline', '&:hover': { opacity: 0.75 } }}
          >
            • {f.description}
          </Typography>
        );
      }
      return <Typography key={i} variant="caption" display="block" sx={{ ml: 1, color }}>• {f.description}</Typography>;
    })
  );

  const round = currentRound(selected);
  const statusInfo = selected ? (STATUS_LABELS[selected.qaStatus] || STATUS_LABELS.PENDIENTE_REVISION) : null;
  const blockingCodesForRound = round
    ? QA_CHECKLIST_SECTIONS.filter(s => s.round === round)
        .flatMap(s => s.items)
        .filter(i => { const f = findings[i.code]; return f && !f.resolved && BLOCKING_STATUSES.has(f.status); })
        .map(i => i.code)
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '85vh', maxHeight: '85vh', display: 'flex', flexDirection: 'column' } }}
    >
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : loadError || !selected ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{loadError || 'Documento no encontrado'}</Alert>
        </Box>
      ) : (
        <>
          {/* Encabezado fijo: identidad del documento, no se mueve al cambiar de tab */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FactCheckIcon sx={{ color: '#7b1fa2', fontSize: 26 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                    Revisión de Calidad — {docLabel(selected.documentKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selected.companyName} — {selected.entityName}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Tooltip title={selected.branchId == null ? 'Matriz' : 'Sucursal'}>
                  <Chip
                    icon={selected.branchId == null
                      ? <AccountBalanceIcon sx={{ fontSize: '14px !important' }} />
                      : <StoreIcon sx={{ fontSize: '14px !important' }} />}
                    label={selected.entityName}
                    size="small"
                    sx={{ bgcolor: selected.branchId == null ? '#ede7f6' : '#e1f5fe', color: selected.branchId == null ? '#4527a0' : '#01579b', fontWeight: 600 }}
                  />
                </Tooltip>
                <Chip label={statusInfo.label} size="small" sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 600 }} />
                {selected.contentChanged === true && (
                  <Tooltip title="Este archivo fue reemplazado manualmente y su contenido difiere de la versión anterior">
                    <Chip label="Modificado" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                  </Tooltip>
                )}
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Tabs
              value={tab}
              onChange={(e, v) => {
                setTab(v);
                // Refrescar al entrar a la pestaña, no solo al abrir el diálogo -- si el usuario
                // reemplaza el documento y luego navega a Versiones/Notas sin cerrar el diálogo,
                // debe ver el dato fresco sin depender de cerrar/reabrir.
                if (v === 'versiones') fetchVersions(quotationDocumentId);
                if (v === 'notas') fetchNotes(quotationDocumentId);
              }}
              sx={{ mt: 1, minHeight: 32 }}
            >
              {TABS.map(t => (
                <Tab key={t.key} value={t.key} label={t.label} icon={t.icon} iconPosition="start" sx={{ textTransform: 'none', minHeight: 32, fontSize: '0.8rem' }} />
              ))}
            </Tabs>
          </Box>

          {/* Contenido según tab activo */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
            {tab === 'documento' && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Archivo: {selected.originalFilename}
                  {selected.uploadedBy && ` — generado por ${selected.uploadedBy}`}
                  {selected.uploadedAt && ` (${formatDateTime(selected.uploadedAt)})`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selected.dropboxLink && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon fontSize="small" />}
                      component="a"
                      href={getDownloadLink(selected.dropboxLink)}
                      sx={{ textTransform: 'none' }}
                    >
                      Descargar
                    </Button>
                  )}
                  <input
                    ref={replaceInputRef}
                    type="file"
                    accept=".docx"
                    style={{ display: 'none' }}
                    onChange={handleReplaceFile}
                  />
                  {/* Constancias: se corrigen regenerándolas desde Programas, no se reemplazan a mano */}
                  {!selected.documentKey?.startsWith('CONSTANCIAS_CURSO_') && (
                  <Tooltip title="Descargar, editar fuera del sistema, y volver a subir con el mismo nombre">
                    <Button
                      variant="outlined"
                      startIcon={replacing ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" />}
                      onClick={() => replaceInputRef.current?.click()}
                      disabled={replacing}
                      sx={{ textTransform: 'none' }}
                    >
                      {replacing ? `Subiendo... ${replaceProgress}%` : 'Reemplazar'}
                    </Button>
                  </Tooltip>
                  )}
                </Box>

                {selected.qaRound1ReviewedAt || selected.qaRound2ReviewedAt ? (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Historial de rondas</Typography>
                    {selected.qaRound1ReviewedAt && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Ronda 1 — {selected.qaRound1ReviewerName || 'Revisor'} — {formatDateTime(selected.qaRound1ReviewedAt)}
                      </Typography>
                    )}
                    {selected.qaRound2ReviewedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Ronda 2 — {selected.qaRound2ReviewerName || 'Revisor'} — {formatDateTime(selected.qaRound2ReviewedAt)}
                      </Typography>
                    )}
                  </>
                ) : null}
              </Box>
            )}

            {tab === 'validacion' && (
              <Box>
              {renderFileComparison()}
              {fieldValidationLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : fieldValidation ? (() => {
                const pct = fieldValidation.completionPercentage || 0;
                const entityType = selected.branchId == null ? 'MATRIZ' : 'SUCURSAL';
                const entityId = selected.branchId == null ? selected.companyId : selected.branchId;
                const ctx = {
                  workOrderId: selected.workOrderId,
                  quotationId: selected.quotationId,
                  companyId: selected.companyId,
                  companyName: selected.companyName,
                };
                const missingReq = groupMissingFieldsBySection(fieldValidation.missingRequired);
                const missingOpt = groupMissingFieldsBySection(fieldValidation.missingOptional);
                const hasReqMissing = Object.keys(missingReq).length > 0;
                const hasOptMissing = Object.keys(missingOpt).length > 0;
                return (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {getReadinessIcon(pct)}
                      <Typography variant="subtitle2" fontWeight={700}>{docLabel(selected.documentKey)}</Typography>
                      <Chip label={`${pct}%`} size="small" sx={{ bgcolor: getReadinessColor(pct), color: '#fff', fontWeight: 700 }} />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 6, borderRadius: 3, mb: 2, bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': { bgcolor: getReadinessColor(pct), borderRadius: 3 }
                      }}
                    />
                    {hasReqMissing && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#ffebee', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight={700} color="error">Campos requeridos faltantes:</Typography>
                        {Object.entries(missingReq).map(([section, fields]) => (
                          <Box key={section} sx={{ ml: 1, mt: 0.75 }}>
                            {renderSectionHeader(section, entityType, entityId, '#d32f2f', fields, ctx)}
                            {renderMissingFieldsList(fields, entityType, entityId, '#d32f2f', ctx)}
                          </Box>
                        ))}
                      </Box>
                    )}
                    {hasOptMissing && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#e65100' }}>Campos opcionales faltantes:</Typography>
                        {(fieldValidation.missingOptional || []).some(f => f.optional) && (
                          <Typography variant="caption" display="block" sx={{ color: '#8d6e63', ml: 1 }}>
                            Los dictámenes de la Evaluación de Inspección <strong>no son obligatorios</strong>: si se capturan salen en el PIPC; si no, quedan en blanco y no impiden generarlo.
                          </Typography>
                        )}
                        {Object.entries(missingOpt).map(([section, fields]) => (
                          <Box key={section} sx={{ ml: 1, mt: 0.75 }}>
                            {renderSectionHeader(section, entityType, entityId, '#ef6c00', fields, ctx)}
                            {renderMissingFieldsList(fields, entityType, entityId, '#ef6c00', ctx)}
                          </Box>
                        ))}
                      </Box>
                    )}
                    {!hasReqMissing && !hasOptMissing && (
                      <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                        Todos los campos completos
                      </Typography>
                    )}
                  </Box>
                );
              })() : (
                <Typography variant="body2" color="text.secondary">
                  No se pudo cargar la validación de campos para este ente.
                </Typography>
              )}
              </Box>
            )}

            {tab === 'checklist' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Criterios de revisión — {round === 1 ? 'Fase I' : round === 2 ? 'Fase II' : 'Sin ronda pendiente'}
                </Typography>
                {findingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                ) : !round ? (() => {
                  // Documento ya sin ronda pendiente (ambas rondas aprobadas) -- en vez de un
                  // callejón sin salida, se muestra el checklist COMPLETO de las dos rondas en
                  // modo solo lectura, a modo de auditoría de qué se encontró y corrigió.
                  const allItems = QA_CHECKLIST_SECTIONS.flatMap(s => s.items);
                  const withFinding = allItems.filter(i => findings[i.code]);
                  const unresolved = withFinding.filter(i => !findings[i.code].resolved);
                  return (
                    <Box>
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Revisión completa — auditoría de ambas rondas
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {withFinding.length} de {allItems.length} criterios con hallazgo
                          {unresolved.length > 0 ? ` · ${unresolved.length} sin resolver` : withFinding.length > 0 ? ' · todos resueltos' : ''}
                        </Typography>
                        {selected.qaRound1ReviewedAt && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Ronda 1 — {selected.qaRound1ReviewerName || 'Revisor'} — {formatDateTime(selected.qaRound1ReviewedAt)}
                          </Typography>
                        )}
                        {selected.qaRound2ReviewedAt && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Ronda 2 — {selected.qaRound2ReviewerName || 'Revisor'} — {formatDateTime(selected.qaRound2ReviewedAt)}
                          </Typography>
                        )}
                      </Box>
                      {QA_CHECKLIST_SECTIONS.map(section => {
                        const sectionWithFinding = section.items.filter(i => findings[i.code]).length;
                        return (
                          <Accordion
                            key={section.code}
                            expanded={expandedSection === section.code}
                            onChange={() => setExpandedSection(prev => prev === section.code ? null : section.code)}
                            disableGutters
                            sx={{ mb: 1, '&:before': { display: 'none' }, border: '1px solid #eee', borderRadius: 1 }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                                <Chip
                                  label={`Fase ${section.round === 1 ? 'I' : 'II'}`}
                                  size="small"
                                  sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 700 }}
                                />
                                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                  {section.code}. {section.label}
                                </Typography>
                                <Chip
                                  label={`${sectionWithFinding}/${section.items.length} con hallazgo`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.68rem', height: 20,
                                    bgcolor: sectionWithFinding === 0 ? '#e8f5e9' : '#f5f5f5',
                                    color: sectionWithFinding === 0 ? '#2e7d32' : '#616161',
                                    fontWeight: 600
                                  }}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {section.items.map(item => {
                                const finding = findings[item.code];
                                const opt = finding ? QA_FINDING_STATUS_OPTIONS.find(o => o.value === finding.status) : null;
                                return (
                                  <Box key={item.code} sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                      <Typography variant="body2" sx={{ flex: 1 }}>
                                        <Typography component="span" variant="caption" fontWeight={700} color="text.secondary" sx={{ mr: 0.5 }}>
                                          {item.code}
                                        </Typography>
                                        {item.text}
                                      </Typography>
                                      {finding ? (
                                        <Chip
                                          label={opt?.label || finding.status}
                                          size="small"
                                          sx={{ fontSize: '0.65rem', height: 20, bgcolor: opt?.bg || '#f5f5f5', color: opt?.color || '#616161', fontWeight: 700, flexShrink: 0 }}
                                        />
                                      ) : (
                                        <Chip label="Cumple" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, flexShrink: 0 }} />
                                      )}
                                      {finding?.resolved ? (
                                        <Tooltip title={`Resuelto por ${finding.resolvedByName || 'alguien'} — ${formatDateTime(finding.resolvedAt)}`}>
                                          <Chip icon={<TaskAltIcon sx={{ fontSize: '14px !important' }} />} label="Resuelto" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, flexShrink: 0 }} />
                                        </Tooltip>
                                      ) : finding ? (
                                        <Chip label="Sin resolver" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, flexShrink: 0 }} />
                                      ) : null}
                                    </Box>
                                    {finding && (
                                      <Box sx={{ ml: 3, mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                          "{finding.note}"
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {finding.createdByName || 'Sistema'} — {formatDateTime(finding.createdAt)}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  );
                })() : QA_CHECKLIST_SECTIONS.filter(s => s.round === round).map(section => {
                  const withFinding = section.items.filter(i => findings[i.code]).length;
                  const blockingOpen = section.items.filter(i => {
                    const f = findings[i.code];
                    return f && !f.resolved && BLOCKING_STATUSES.has(f.status);
                  }).length;
                  return (
                    <Accordion
                      key={section.code}
                      expanded={expandedSection === section.code}
                      onChange={() => setExpandedSection(prev => prev === section.code ? null : section.code)}
                      disableGutters
                      sx={{ mb: 1, '&:before': { display: 'none' }, border: '1px solid #eee', borderRadius: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                            {section.code}. {section.label}
                          </Typography>
                          {blockingOpen > 0 && (
                            <Chip label={`${blockingOpen} bloquean`} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} />
                          )}
                          <Chip
                            label={`${withFinding}/${section.items.length} con hallazgo`}
                            size="small"
                            sx={{
                              fontSize: '0.68rem', height: 20,
                              bgcolor: withFinding === 0 ? '#e8f5e9' : '#f5f5f5',
                              color: withFinding === 0 ? '#2e7d32' : '#616161',
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {section.items.map(item => {
                          const finding = findings[item.code];
                          const draft = getDraft(item.code);
                          const dirty = isDraftDirty(item.code);
                          const busy = !!findingBusy[item.code];
                          const blocks = finding && !finding.resolved && BLOCKING_STATUSES.has(finding.status);
                          return (
                            <Box key={item.code} sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Typography component="span" variant="caption" fontWeight={700} color="text.secondary" sx={{ mr: 0.5 }}>
                                    {item.code}
                                  </Typography>
                                  {item.text}
                                </Typography>
                                {blocks && (
                                  <Chip label="Bloquea" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} />
                                )}
                                {finding?.resolved && (
                                  <Tooltip title={`Resuelto por ${finding.resolvedByName || 'alguien'} — ${formatDateTime(finding.resolvedAt)}`}>
                                    <Chip icon={<TaskAltIcon sx={{ fontSize: '14px !important' }} />} label="Resuelto" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                                  </Tooltip>
                                )}
                                <ToggleButtonGroup
                                  size="small"
                                  exclusive
                                  disabled={busy}
                                  value={draft.status}
                                  onChange={(e, val) => handleDraftStatus(item.code, val)}
                                  sx={{ flexShrink: 0 }}
                                >
                                  {QA_FINDING_STATUS_OPTIONS.map(opt => (
                                    <ToggleButton
                                      key={opt.value}
                                      value={opt.value}
                                      title={opt.title}
                                      sx={{
                                        px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 700, textTransform: 'none',
                                        '&.Mui-selected': { bgcolor: opt.bg, color: opt.color, '&:hover': { bgcolor: opt.bg } }
                                      }}
                                    >
                                      {opt.label}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              </Box>
                              {draft.status && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.75, ml: 3 }}>
                                  <TextField
                                    size="small" fullWidth multiline minRows={1}
                                    placeholder={draft.status === 'NA' ? 'Justificación (obligatoria)...' : 'Nota (obligatoria)...'}
                                    value={draft.note}
                                    disabled={busy}
                                    onChange={e => handleDraftNote(item.code, e.target.value)}
                                  />
                                  {dirty && (
                                    <Tooltip title="Guardar hallazgo">
                                      <span>
                                        <IconButton size="small" disabled={busy || !draft.note.trim()} onClick={() => handleSaveFinding(item.code)}>
                                          <SaveIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {finding && !dirty && (
                                    <Tooltip title="Quitar hallazgo (volver a Cumple)">
                                      <span>
                                        <IconButton size="small" disabled={busy} onClick={() => handleClearFinding(item.code)}>
                                          <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {blocks && !dirty && (
                                    <Button size="small" disabled={busy} onClick={() => handleResolveFinding(item.code)} sx={{ textTransform: 'none', flexShrink: 0 }}>
                                      Marcar resuelto
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}

            {tab === 'versiones' && (
              versionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
              ) : versions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Este documento no ha sido reemplazado todavía. Cada vez que se reemplace manualmente, la versión anterior queda archivada aquí.
                </Typography>
              ) : versions.map(v => (
                <Box key={v.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      <Chip label={`Versión ${v.versionNumber}`} size="small" sx={{ bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 600, fontSize: '0.68rem' }} />
                      {v.contentChanged === true && (
                        <Chip label="Modificado" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.68rem' }} />
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{v.originalFilename}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reemplazado por {v.replacedByName || 'Sistema'} — {formatDateTime(v.replacedAt)}
                      {v.fileSize != null && ` — ${formatFileSize(v.fileSize)}`}
                    </Typography>
                  </Box>
                  {v.downloadLink ? (
                    <Tooltip title="Descargar esta versión">
                      <Button
                        size="small"
                        startIcon={<DownloadIcon fontSize="small" />}
                        component="a"
                        href={getDownloadLink(v.downloadLink)}
                        sx={{ textTransform: 'none', flexShrink: 0 }}
                      >
                        Descargar
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title="El archivo de esta versión ya no está disponible">
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>No disponible</Typography>
                    </Tooltip>
                  )}
                </Box>
              ))
            )}

            {tab === 'notas' && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  {notesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                  ) : notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Sin notas todavía.</Typography>
                  ) : notes.map(n => n.eventType === 'QA_DOCUMENT_REPLACED' ? (
                    <Box key={n.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                      <Divider sx={{ flex: 1 }} />
                      <Tooltip title={n.note}>
                        <Chip
                          icon={<SwapHorizIcon sx={{ fontSize: '14px !important' }} />}
                          label={`Documento reemplazado — ${formatDateTime(n.createdAt)}`}
                          size="small"
                          sx={{ bgcolor: '#e3f2fd', color: '#01579b', fontWeight: 600, fontSize: '0.68rem' }}
                        />
                      </Tooltip>
                      <Divider sx={{ flex: 1 }} />
                    </Box>
                  ) : (
                    <Box key={n.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {n.authorName || 'Sistema'}
                          {n.round && (
                            <Chip
                              label={`Ronda ${n.round} — ${n.decision === 'APROBADO' ? 'Aprobado' : 'Rechazado'}`}
                              size="small"
                              sx={{
                                ml: 1, height: 18, fontSize: '0.65rem',
                                bgcolor: n.decision === 'APROBADO' ? '#e8f5e9' : '#ffebee',
                                color: n.decision === 'APROBADO' ? '#2e7d32' : '#c62828'
                              }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(n.createdAt)}</Typography>
                      </Box>
                      <Typography variant="body2">{n.note}</Typography>
                    </Box>
                  ))}
                </Box>

                <TextField
                  fullWidth multiline rows={2} size="small"
                  placeholder="Escribe una nota (opcional)..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<SendIcon fontSize="small" />}
                    disabled={!newNote.trim() || actionLoading}
                    onClick={handleAddNote}
                    sx={{ textTransform: 'none' }}
                  >
                    Agregar nota
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {/* Pie fijo con las acciones de ronda, visible sin importar el tab activo */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
            {round && (
              <>
                <Button
                  variant="outlined" color="error" startIcon={<CancelIcon />}
                  disabled={actionLoading}
                  onClick={() => handleReview(false)}
                  sx={{ textTransform: 'none' }}
                >
                  Rechazar ronda {round}
                </Button>
                <Tooltip title={blockingCodesForRound.length > 0
                  ? `Hay hallazgos sin resolver: ${blockingCodesForRound.join(', ')}`
                  : ''}
                >
                  <span>
                    <Button
                      variant="contained" color="success" startIcon={<CheckCircleIcon />}
                      disabled={actionLoading || blockingCodesForRound.length > 0}
                      onClick={() => handleReview(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Aprobar ronda {round}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default RevisionCalidadDialog;
